import { Box, Button, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import DatePicker from 'src/components/DatePicker';
import { Filters } from 'src/components/Filters';
import SwitchButton from 'src/components/widgets/buttons/SwitchButton';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { ClearIcon } from 'src/icons/Clear';
import useHasPermissions from 'src/hooks/useHasPermission';
import { APPROVALS, CLIENT_CASES } from 'src/constants/constants';
// import Tune from '@mui/icons-material/Tune';
// import AdvancedSearchModal from '../widgets/modal/AdvancedSearchModal';

interface CasesSubHeaderProps {
    search: string;
    onSearch: (value: string) => void;
    filters: any;
    onRemoveFilter: (column: string) => void;
    hasAttachments: boolean;
    onSwitchChange: (checked: boolean) => void;
    startDate: Date | null;
    onStartDateChange: (date: Date | null) => void;
    endDate: Date | null;
    onEndDateChange: (date: Date | null) => void;
    onClearFilters: () => void;
    extraFilters?: ReactNode;
}

const CasesSubHeader = ({
    search,
    onSearch,
    filters,
    onRemoveFilter,
    hasAttachments,
    onSwitchChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    onClearFilters,
    extraFilters,
}: CasesSubHeaderProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const checkPermissions = useHasPermissions();
    const whiteLabel: any = JSON.parse(localStorage.getItem('white_label'));
    // const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    // Determine active tab based on current route
    const getCurrentTab = () => {
        const { pathname } = location;
        if (pathname.includes('/cases/all')) return 'all';
        if (pathname.includes('/cases/open')) return 'open';
        if (pathname.includes('/cases/approvals')) return 'approvals';
        if (pathname.includes('/cases/backlog')) return 'backlog';
        if (pathname.includes('/cases/testing')) return 'testing';
        return 'all'; // default
    };

    const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
        navigate(`/cases/${newValue}`);
    };

    return (
        <Box>
            {/* Title Row */}
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px',
                    marginBottom: '16px',
                }}>
                {/* Title */}
                <Typography
                    variant="h5"
                    style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: '#1A202C',
                        whiteSpace: 'nowrap',
                    }}>
                    Tickets
                </Typography>
            </Box>

            {/* Search and Filters and Tabs */}
            <Box style={{ marginBottom: '10px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        gap: '16px',
                    }}>
                    {/* Tabs */}
                    <Tabs
                        value={getCurrentTab()}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 'unset',
                            '& .MuiTabs-indicator': {
                                height: '2px',
                            },
                            '& .MuiTab-root': {
                                minHeight: 'unset',
                                minWidth: 'unset',
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#425466',
                                padding: '6px 12px',
                            },
                            '& .MuiTab-root.Mui-selected': {
                                color: '#1A202C',
                            },
                        }}>
                        {checkPermissions([CLIENT_CASES]) && <Tab label="All" value="all" />}
                        {checkPermissions([CLIENT_CASES]) && <Tab label="Open" value="open" />}
                        {(whiteLabel?.isApprovalRequired && checkPermissions([APPROVALS])) && (
                            <Tab label="Approvals" value="approvals" />
                        )}
                        {checkPermissions([CLIENT_CASES]) && <Tab label="Backlog" value="backlog" />}
                        <Tab label="Testing" value="testing" />
                    </Tabs>

                    <Stack
                        direction="row"
                        spacing={2}
                        divider={
                            <Divider
                                orientation="vertical"
                                flexItem
                                style={{
                                    height: '22px',
                                    alignSelf: 'center',
                                }}
                            />
                        }
                        alignItems="center"
                    >
                        <Box display="flex" alignItems="center" gap="10px">
                            <SearchBar
                                onSearch={onSearch}
                                value={search}
                                placeholderData="Search ..."
                            />
                            <Typography
                                style={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: '#8492A6',
                                }}>
                                TICKETS WITH ATTACHMENTS
                            </Typography>
                            <SwitchButton
                                checked={hasAttachments}
                                onChange={(event) => onSwitchChange(event.target.checked)}
                            />
                        </Box>
                        {extraFilters}
                        <DatePicker
                            startDate={startDate}
                            setStartDate={onStartDateChange}
                            endDate={endDate}
                            setEndDate={onEndDateChange}
                        />
                        {/* <Button
                            color="inherit"
                            style={{
                                minWidth: 'unset',
                                fontSize: 12,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                backgroundColor: '#fff',
                                paddingX: '16px',
                                // border: `1px solid ${theme.palette.divider}`,
                            }}
                            startIcon={<Tune />}
                            onClick={() => setShowAdvancedSearch(true)}
                        >
                            Advanced Search
                        </Button>
                        {showAdvancedSearch && (
                            <AdvancedSearchModal
                                _headingMain={"Advanced Search"}
                                _setShowAdvancedSearch={setShowAdvancedSearch}
                                _search={search}
                                _setSearch={onSearch}
                                _caseType={[]}
                                _setCaseType={() => { }}
                                _priority={[]}
                                _setPriority={() => { }}
                                _project={null}
                                _setProject={() => { }}
                                _status={[]}
                                _setStatus={() => { }}
                                _hasAttachments={hasAttachments}
                                _setHasAttachments={(checked: boolean) => onSwitchChange(checked)}
                                _handleReset={onClearFilters}
                                _date={{ from: startDate, to: endDate }}
                                _setDate={({ from, to }) => {
                                    onStartDateChange(from ? new Date(from) : null);
                                    onEndDateChange(to ? new Date(to) : null);
                                }}
                                _setPage={() => { }}
                            />
                        )} */}
                        <Button
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                display: 'flex',
                                gap: '5px',
                                flexShrink: 0,
                            }}
                            onClick={onClearFilters}>
                            <ClearIcon />
                            <span
                                style={{
                                    marginLeft: '5px',
                                    color: '#425466',
                                    fontSize: '13px',
                                }}>
                                Clear All
                            </span>
                        </Button>
                    </Stack>
                </Box>
                {Object.keys(filters).length > 0 && (
                    <Box style={{ marginTop: '8px' }}>
                        <Filters filters={filters} handleRemoveFilter={onRemoveFilter} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CasesSubHeader;